import {
  faClose,
  faInfoCircle,
  faRobot,
} from "@fortawesome/pro-regular-svg-icons";
import { m } from "framer-motion";
import { FormProvider, useForm } from "react-hook-form";
import useMeasure from "react-use-measure";
import { useDebounceCallback } from "usehooks-ts";

import { Flow, FlowVersion, FlowVersionStatus } from "src/api/flowTypes";
import { Dataset } from "src/api/types";
import { Divider } from "src/base-components/Divider";
import { BeMappedNode } from "src/constants/NodeDataTypes";
import { DatasetSelector } from "src/datasets/DatasetSelector";
import { Callout } from "src/design-system/Callout";
import { Icon, IconButton } from "src/design-system/Icon";
import { Tooltip } from "src/design-system/Tooltip";
import {
  LongTestRunConfigForm,
  LongTestRunsConfig,
} from "src/flowContainer/RunFlowButton/LongTestRunsConfig";
import { MultiversionConfig } from "src/flowContainer/RunFlowButton/MultiversionConfig";
import { AdditionalVersionSelector } from "src/flowContainer/RunFlowButton/RunFlowButton";
import { RunFlowWarning } from "src/flowContainer/RunFlowButton/RunFlowWarning";
import { getSuggestedConcurrenyLimit } from "src/flowContainer/RunFlowButton/utils";
import { useIsIcebergEnabled } from "src/hooks/useIsIcebergEnabled";
import { useUpdateTestRunJobConfig } from "src/jobs/api/queries";
import { Job, JobConfiguration, JobRateLimitInterval } from "src/jobs/types";
import { FEATURE_FLAGS, isFeatureFlagEnabled } from "src/router/featureFlags";
import { useSimulationAccess } from "src/simulations/useSimulationAccess";
import { useGraphStore } from "src/store/StoreProvider";
import { useSubmitForm } from "src/utils/useSubmitForm";

type RunTestDropdownProps = {
  datasets: Dataset[];
  isLoading: boolean;
  selectedDatasetId: string | null;
  testRunJob: Job;
  onSelectData: (datasetId: string) => void;
  onClose: () => void;
  flow: Flow;
  version: FlowVersion;
  additionalVersions: AdditionalVersionSelector[];
  hasAgentNodes: boolean;
  setAdditionalVersions: (value: AdditionalVersionSelector[]) => void;
  onGoToSimulations: () => void;
};

export const RunTestDropdownWrapper: React.FC<{
  children: React.ReactNode;
}> = ({ children }) => {
  // The height of this component is animated as
  // additional versions are added or removed
  const [ref, { height }] = useMeasure({
    offsetSize: true,
  });

  return (
    <m.div
      animate={{
        height: height === 0 ? "auto" : height,
      }}
      className="overflow-hidden"
      initial={false}
    >
      <div ref={ref} className="w-122 cursor-default pt-4 pb-6">
        {children}
      </div>
    </m.div>
  );
};

const buildDefaultValues = ({
  jobConfig,
  selectedDataset,
  nodesArray,
}: {
  jobConfig: JobConfiguration | null;
  selectedDataset: Dataset | null;
  nodesArray: BeMappedNode[];
}): LongTestRunConfigForm => {
  const suggestedConcurrenyConfig = getSuggestedConcurrenyLimit(
    selectedDataset?.row_count ?? 0,
    nodesArray,
  );

  if (!jobConfig) {
    return {
      limitType: "rate-limit",
      ...suggestedConcurrenyConfig,
      rateLimit: 5,
      rateLimitInterval: JobRateLimitInterval.MINUTE,
    };
  }

  return {
    limitType:
      jobConfig.execution_concurrency === null ? "rate-limit" : "concurrency",
    executionConcurrency: jobConfig.execution_concurrency
      ? jobConfig.execution_concurrency
      : suggestedConcurrenyConfig.executionConcurrency,
    executionMode: jobConfig.execution_mode,
    rateLimit: jobConfig.rate_limit ?? 5,
    rateLimitInterval:
      jobConfig.rate_limit_interval ?? JobRateLimitInterval.MINUTE,
  };
};

export const RunTestDropdown: React.FC<RunTestDropdownProps> = ({
  testRunJob,
  datasets,
  selectedDatasetId,
  isLoading,
  flow,
  version,
  additionalVersions,
  hasAgentNodes,
  onSelectData,
  onClose,
  setAdditionalVersions,
  onGoToSimulations,
}) => {
  const isIcebergEnabled = useIsIcebergEnabled();
  const selectedDataset = datasets.find((d) => d.id === selectedDatasetId);
  const { nodesArray } = useGraphStore();
  const { mutateAsync: updateTestRunJobConfig, isPending } =
    useUpdateTestRunJobConfig({
      flowId: flow.id,
      flowVersionId: version.id,
    });

  const longTestRunConfigForm = useForm<LongTestRunConfigForm>({
    defaultValues: buildDefaultValues({
      jobConfig: testRunJob.configuration ?? null,
      selectedDataset: selectedDataset ?? null,
      nodesArray,
    }),
  });

  const handleConfigChange = useDebounceCallback(async (values) => {
    const effectiveConfiguration =
      values.limitType === "rate-limit"
        ? ({
            execution_mode: values.executionMode,
            execution_concurrency: null,
            rate_limit: values.rateLimit,
            rate_limit_interval: values.rateLimitInterval,
          } as const)
        : ({
            execution_mode: values.executionMode,
            rate_limit: null,
            rate_limit_interval: null,
            execution_concurrency: values.executionConcurrency,
          } as const);

    await updateTestRunJobConfig({
      jobId: testRunJob.id,
      etag: testRunJob.etag,
      configuration: effectiveConfiguration,
    });
  }, 200);

  useSubmitForm({
    disabled: isPending,
    watch: longTestRunConfigForm.watch,
    onChange: handleConfigChange,
  });

  const effectiveIsLongTestRunsEnabled =
    isIcebergEnabled &&
    (Boolean(version.meta.enable_long_test_runs) || hasAgentNodes);
  const simulationAccess = useSimulationAccess();
  const availableVersions = flow.versions.filter(
    (v) =>
      v.status !== FlowVersionStatus.ARCHIVED &&
      v.id !== version.id &&
      !additionalVersions.some((selector) => selector.version.id === v.id),
  );

  return (
    <RunTestDropdownWrapper>
      <div className="flex items-center justify-between border-gray-100 border-b px-5 pb-3">
        <p className="font-inter-semibold-16px text-gray-800">Test run</p>
      </div>
      <div className="absolute top-4 right-4">
        <IconButton
          dataLoc="close-test-run-dropdown"
          icon={faClose}
          size="sm"
          onClick={onClose}
        />
      </div>
      <div className="flex flex-col gap-y-5 px-5 pt-4">
        <div>
          <div className="mb-2 flex">
            <p className="font-inter-semibold-13px text-gray-800">Dataset</p>
            <Tooltip
              body="Test this version of the Decision Flow using test data to fine-tune the flow and its steps."
              placement="top-start"
              asChild
            >
              <span className="ml-0.5">
                <Icon icon={faInfoCircle} size="xs" />
              </span>
            </Tooltip>
          </div>
          <DatasetSelector
            datasets={datasets}
            isLoading={isLoading}
            selectedDatasetId={selectedDatasetId}
            onSelectData={onSelectData}
          />
          <RunFlowWarning
            flow={flow}
            selectedDatasetId={selectedDatasetId}
            version={version}
          />
        </div>
        {isIcebergEnabled && isFeatureFlagEnabled(FEATURE_FLAGS.powertools) && (
          <FormProvider {...longTestRunConfigForm}>
            <LongTestRunsConfig forceEnabled={hasAgentNodes} />
          </FormProvider>
        )}
        {!effectiveIsLongTestRunsEnabled && (
          <div className="flex flex-col gap-y-2">
            <div>
              <p className="font-inter-medium-13px">Versions</p>
              <p className="font-inter-normal-12px text-gray-500">
                You can select multiple versions to compare results and metrics
              </p>
            </div>
            <MultiversionConfig
              additionalVersions={additionalVersions}
              availableVersions={availableVersions}
              version={version}
              onAddAdditionalVersion={setAdditionalVersions}
            />
          </div>
        )}
        {effectiveIsLongTestRunsEnabled && (
          <Callout type="neutral" icon={faRobot}>
            <p>This test run will include Agent nodes testing</p>
          </Callout>
        )}
        {simulationAccess.canAccess && (
          <>
            <Divider />
            <Callout
              action={{
                text: "Open simulations",
                onClick: onGoToSimulations,
              }}
              type="neutral"
            >
              Looking to test with large datasets?
            </Callout>
          </>
        )}
      </div>
    </RunTestDropdownWrapper>
  );
};
